import { useState, useEffect } from 'react'
import { ImageSourcePropType } from 'react-native'
import { useCurrentUserStore } from '@store'
import { ContactListItem, Scope } from '@utils'
import { useNavigation } from '@react-navigation/native'
import { Profile, useCreateGhostProfile, useUpdateGhostProfile, useUpdateProfile } from '@hooks'
import dayjs from 'dayjs'
import { days } from '@components'

export const useAddGhostContact = (profile?: Profile) => {
  const navigation = useNavigation()
  const { mutate: updateProfile } = useUpdateProfile()
  const { mutate: createGhostProfile } = useCreateGhostProfile()
  const { mutate: updateGhostProfile } = useUpdateGhostProfile()

  const { userID } = useCurrentUserStore()
  const isMe = profile?.accountId === userID
  const isGhost = profile?.isGhost
  
  const [formData, setFormData] = useState<ContactListItem>({
    id: '',
    firstName: '',
    lastName: '',
    fullName: '',
    email: '',
    phone: '',
    company: '',
    professions: [],
    notes: '',
    gender: 'female',
    birthDate: '',
    website: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: '',
    about: '',
    hobbies: [],
    languages: [],
    goals: [],
    hashtags: [],
    pinsHashtags: [],
    pinsCategories: [],
    residenceHistory: [],
  })

  // Initialize form data with profile values when editing
  useEffect(() => {
    if (profile) {
      // Convert birthDate from ISO format to DD-MM-YYYY format
      const formattedBirthDate = profile.birthDate 
        ? dayjs(profile.birthDate).format('DD-MM-YYYY')
        : ''
      
      setFormData({
        id: profile.id || '',
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        fullName: profile.fullName || '',
        email: profile.contacts?.email || '',
        phone: profile.contacts?.phone || '',
        company: (profile as { company?: string }).company || '',
        professions: profile.professions?.map(profession => ({ id: profession.id, name: profession.name })) || [],
        notes: '',
        gender: (profile.gender as any) || 'female',
        birthDate: formattedBirthDate,
        website: profile.contacts?.website || '',
        facebook: profile.contacts?.facebook || '',
        instagram: profile.contacts?.instagram || '',
        linkedin: profile.contacts?.linkedin || '',
        twitter: profile.contacts?.twitter || '',
        about: profile.bio || '',
        hobbies: profile.hobbies || [],
        languages: profile.languages?.map(language => ({ id: language.code, name: language.name ?? language.nameFr })) || [],
        nationalities: profile.nationalities?.map(nationality => ({ id: nationality.code, name: nationality.name ?? nationality.nameFr })) || [],
        origins: (profile.origins || [])
          .map((origin) => ({ id: origin.code, name: origin.name ?? origin.nameFr })),
        residence: profile.residence ? { id: profile.residence.code, name: profile.residence.name ?? profile.residence.nameFr } : undefined,
        residenceHistory: profile.residenceHistory?.map(residence => ({ id: residence.code, name: residence.name ?? residence.nameFr })) || [],
        goals: profile.goals || [],
        skills: profile.skills || [],
        hashtags: profile.hashtags || [],
        pinsHashtags: profile.pinsHashtags || [],
        pinsCategories: profile.pinsCategories || [],
        bestMoments: profile.availabilities?.moments|| [],
        disponibilities: profile.availabilities?.slots?.map(slot => ({
          dayStart: days.find(day => day.id === slot.days[0].toString())!,
          dayEnd: days.find(day => day.id === slot.days[slot.days.length - 1].toString())!,
          timeStart: slot.from,
          timeEnd: slot.to,
        })) || [],
      })
    }
  }, [profile])

  const handleFormChange = (field: keyof ContactListItem, value: string | string[] | any | ImageSourcePropType | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = (scope?: Scope[]) => {
    // Map bestMoments to momentIds (bestMoments are BestMoment objects with id and name)
      const momentIds = formData.bestMoments?.map(moment => moment.id) || []
        
      // Map disponibilities to slots format
      const slots = formData.disponibilities?.map(disp => {
        const startDay = parseInt(disp.dayStart.id, 10)
        const endDay = parseInt(disp.dayEnd.id, 10)
        const days = Array.from({ length: endDay - startDay + 1 }, (_, i) => startDay + i)
        
        return {
          days,
          from: disp.timeStart,
          to: disp.timeEnd,
        }
      }) || []

      const toNullIfEmpty = (v?: string) => (v?.trim() ? v.trim() : null)

      const data = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        bio: formData.about,
        company: formData.company,
        avatar: (formData.avatar as { uri: string } | null) || undefined,
        professionIds: formData.professions?.map(profession => profession.id),
        birthDate: formData.birthDate ? dayjs(formData.birthDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : undefined,
        contacts: {
          email: toNullIfEmpty(formData.email),
          phone: toNullIfEmpty(formData.phone),
          website: toNullIfEmpty(formData.website),
          facebook: toNullIfEmpty(formData.facebook),
          instagram: toNullIfEmpty(formData.instagram),
          linkedin: toNullIfEmpty(formData.linkedin),
          twitter: toNullIfEmpty(formData.twitter),
        },
        languages: formData.languages?.map(language => language.id),
        nationalities: formData.nationalities?.map(nationality => nationality.id),
        origins: (formData.origins || []).map(origin => origin.id),
        residence: formData.residence?.id,
        residenceHistory: formData.residenceHistory?.map(residence => residence.id),
        availabilities: {
          momentIds,
          slots,
        },
        hashtags: formData.hashtags || [],
        goals: formData.goals || [],
        hobbies: formData.hobbies || [],
        skills: formData.skills || [],
      }
    
    if (profile) {
      if(isMe){
        updateProfile(data)
      }
      else if(isGhost) {
        updateGhostProfile({ id: profile.id, ...data,
          pinsHashtags: formData.pinsHashtags?.map(hashtag => hashtag.id),
          pinsCategories: formData.pinsCategories?.map(category => category.id),
        })
      }
    } else {
      const primaryScope = scope?.find(s => s !== Scope.GHOST)
      createGhostProfile({
        ...data,
        ...(primaryScope ? { scope: primaryScope } : {}),
        pinsHashtags: formData.pinsHashtags?.map(hashtag => hashtag.id),
        pinsCategories: formData.pinsCategories?.map(category => category.id),
      })
    } /*else if(scope){
      // Add new contact
      addContact({
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`,
        avatarUrl: `https://randomuser.me/api/portraits/${formData.gender === 'male' ? 'men' : 'women'}/${Math.floor(Math.random() * 10) + 1}.jpg`,
      }, scope)
    }*/
    // Return to the previous screen after saving; fall back to the app home
    // when there's no history (direct link / reload), otherwise goBack() fails
    // silently and the user thinks nothing was saved.
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.reset({ index: 0, routes: [{ name: 'Tab' as never }] })
    }
  }

  return {
    formData,
    handleSave,
    handleFormChange
  }
} 